import type { GameEvent } from "@engine/games/recordEvent.js";
import { BACKPACK_DECK, type BackpackRound } from "./decks.js";

export type GameContext = {
  emit: (event: GameEvent) => void;
  done: () => void;
};

export function mountBackpack(
  root: HTMLElement,
  ctx: GameContext,
  roundIndex: number = 0,
  deck: readonly BackpackRound[] = BACKPACK_DECK
): () => void {
  const safeIdx = deck.length > 0 ? ((roundIndex % deck.length) + deck.length) % deck.length : 0;
  const round = deck[safeIdx];
  if (!round) {
    root.innerHTML = "<p>Backpack deck unavailable.</p>";
    return () => {
      root.innerHTML = "";
    };
  }

  const selected = new Set<number>();
  const chipButtons: HTMLButtonElement[] = [];
  const listeners: Array<() => void> = [];
  let isUnmounted = false;

  const card = document.createElement("section");
  card.className = "card bp-board";

  const label = document.createElement("span");
  label.className = "topic";
  label.textContent = "Fill the Backpack";
  card.appendChild(label);

  const goal = document.createElement("p");
  goal.className = "stem bp-goal";
  goal.textContent = round.goal;
  card.appendChild(goal);

  const bag = document.createElement("div");
  bag.className = "bp-bag";
  bag.setAttribute("role", "group");
  bag.setAttribute("aria-label", "Prompt chips");
  card.appendChild(bag);

  const actions = document.createElement("div");
  actions.className = "actions";
  card.appendChild(actions);

  const submit = document.createElement("button");
  submit.type = "button";
  submit.className = "btn-primary bp-submit";
  actions.appendChild(submit);

  function refreshUi(): void {
    for (let idx = 0; idx < chipButtons.length; idx++) {
      const inBag = selected.has(idx);
      const button = chipButtons[idx];
      if (!button) continue;
      button.classList.toggle("is-inbag", inBag);
      button.setAttribute("aria-pressed", inBag ? "true" : "false");
    }
    submit.textContent = `Submit (${selected.size}/${round.chips.length})`;
  }

  function emitRound(): void {
    if (isUnmounted) return;
    const weight = 1 / round.chips.length;
    for (let idx = 0; idx < round.chips.length; idx++) {
      const fragment = round.chips[idx];
      if (!fragment) continue;
      const event: GameEvent = {
        conceptId: fragment.conceptId,
        correct: selected.has(idx) === fragment.belongsInPrompt,
        weight,
        gameType: "backpack",
      };
      ctx.emit(event);
    }
    ctx.done();
  }

  for (let idx = 0; idx < round.chips.length; idx++) {
    const chip = round.chips[idx];
    if (!chip) continue;
    const button = document.createElement("button");
    button.type = "button";
    button.className = "bp-chip";
    button.textContent = chip.text;
    button.setAttribute("aria-pressed", "false");
    const onToggle = () => {
      if (selected.has(idx)) selected.delete(idx);
      else selected.add(idx);
      refreshUi();
    };
    button.addEventListener("click", onToggle);
    listeners.push(() => button.removeEventListener("click", onToggle));
    chipButtons.push(button);
    bag.appendChild(button);
  }

  submit.addEventListener("click", emitRound);
  listeners.push(() => submit.removeEventListener("click", emitRound));

  root.innerHTML = "";
  root.appendChild(card);
  refreshUi();

  return () => {
    isUnmounted = true;
    for (const off of listeners) off();
    root.innerHTML = "";
  };
}
